import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, ExternalLink, Link2, Pencil } from 'lucide-react';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/hooks/useAuth';
import { useMenu } from '@/hooks/useMenu';
import { useToast } from '@/hooks/useToast';
import { dinero, fecha, numero } from '@/lib/formato-tienda';
import { viendoComo } from '@/lib/ver-como';
import { cn } from '@/lib/cn';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { conCuentas, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import type { Periodo } from '@/api/tienda.api';
import * as api from '@/api/publicidad.api';
import type { FilaDeAnuncio, Veredicto } from '@/api/publicidad.api';

/**
 * PUBLICIDAD — el analista de anuncios: de qué anuncio vienen los chats, de qué calidad, cuántos
 * cierran, cuánto venden y, con el costo anotado, si se pagan solos. El veredicto (se queda, se
 * va, vigilar) sale de reglas que se leen en su «por qué». Silva Service 360 lee lo mismo por un
 * enlace con token que crea la administración.
 *
 * En Rich Real Estate la ven Richard y la administración; el bloque del enlace, solo la
 * administración del sistema (`admin@…`): Richard es OWNER de su negocio y aun así no le sale.
 * Las ventas no se registran aquí: «Vendido» queda en 0 y el veredicto sale de los cierres.
 */

const PERIODOS: Array<{ id: Periodo; etiqueta: string }> = [
  { id: 'hoy', etiqueta: 'Hoy' },
  { id: 'semana', etiqueta: 'Esta semana' },
  { id: 'mes', etiqueta: 'Este mes' },
];

const VEREDICTO: Record<Veredicto, { etiqueta: string; clase: string }> = {
  SE_QUEDA: { etiqueta: 'Se queda', clase: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  SE_VA: { etiqueta: 'Se va', clase: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  VIGILAR: { etiqueta: 'Vigilar', clase: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  FALTA_DATA: { etiqueta: 'Falta data', clase: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
};

const pct = (n: number | null) => (n == null ? '—' : `${n} %`);
const veces = (n: number | null) => (n == null ? '—' : `${n.toLocaleString('es-DO')}×`);

function Calidad({ f }: { f: FilaDeAnuncio }) {
  if (f.leads === 0) return <span className="text-slate-400">—</span>;
  const partes = [
    { n: f.cerrados, clase: 'bg-green-600', nombre: 'cerrados' },
    { n: f.calientes, clase: 'bg-orange-500', nombre: 'calientes' },
    { n: f.tibios, clase: 'bg-amber-300', nombre: 'tibios' },
    { n: f.frios, clase: 'bg-sky-300', nombre: 'fríos' },
    { n: f.sinCalificar, clase: 'bg-slate-200 dark:bg-slate-700', nombre: 'sin calificar' },
  ];
  const titulo = partes.map((p) => `${p.n} ${p.nombre}`).join(' · ');
  return (
    <div className="flex items-center justify-end gap-2" title={titulo}>
      <div className="flex h-2 w-20 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" aria-label={titulo}>
        {partes.map((p) => (p.n > 0 ? <span key={p.nombre} className={p.clase} style={{ width: `${(p.n / f.leads) * 100}%` }} /> : null))}
      </div>
      <span className="w-7 text-right tabular-nums">{f.calidad ?? '—'}</span>
    </div>
  );
}

function EnlaceSilvaService() {
  const toast = useToast();
  const qc = useQueryClient();
  const [token, setToken] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ['publicidad', 'enlace'], queryFn: api.enlace });
  const crear = useMutation({
    mutationFn: api.crearEnlace,
    onSuccess: (r) => {
      setToken(r.token);
      void qc.invalidateQueries({ queryKey: ['publicidad', 'enlace'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo crear el enlace'),
  });
  const quitar = useMutation({
    mutationFn: api.quitarEnlace,
    onSuccess: () => {
      setToken(null);
      void qc.invalidateQueries({ queryKey: ['publicidad', 'enlace'] });
      toast.success('Enlace apagado: el token de antes ya no sirve');
    },
  });
  const direccion = `${window.location.origin}/api/publicidad/reporte`;
  const copiar = (t: string) =>
    navigator.clipboard.writeText(t).then(
      () => toast.success('Copiado'),
      () => toast.error('No se pudo copiar'),
    );

  return (
    <section className="space-y-3 rounded-lg border border-surface-border bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
            <Link2 className="h-4 w-4" /> Enlace para Silva Service 360
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {data?.activo
              ? `Activo desde ${fecha(data.creadoAt, true)}. Crear uno nuevo apaga el anterior.`
              : 'Silva Service 360 lee este mismo reporte con un token de este negocio. Solo lo ve la administración.'}
          </p>
        </div>
        <div className="flex gap-2">
          {data?.activo && (
            <Button variant="outline" size="sm" onClick={() => quitar.mutate()} loading={quitar.isPending}>
              Apagar
            </Button>
          )}
          <Button size="sm" onClick={() => crear.mutate()} loading={crear.isPending}>
            {data?.activo ? 'Crear token nuevo' : 'Crear token'}
          </Button>
        </div>
      </div>
      {token && (
        <div className="space-y-2 rounded-md bg-amber-50 p-3 text-xs dark:bg-amber-900/20">
          <p className="font-medium text-amber-900 dark:text-amber-200">Cópialo ahora: no se vuelve a enseñar.</p>
          {[
            ['Dirección', direccion],
            ['Cabecera', `Authorization: Bearer ${token}`],
          ].map(([t, v]) => (
            <div key={t} className="flex items-center gap-2">
              <span className="w-20 shrink-0 text-slate-500">{t}</span>
              <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 dark:bg-slate-900">{v}</code>
              <button type="button" onClick={() => void copiar(v!)} className="p-1 text-slate-500 hover:text-slate-800" aria-label={`Copiar ${t}`}>
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <p className="text-slate-500">Acepta `?desde=` y `?hasta=` (fechas). Sin ellas, los últimos 30 días.</p>
        </div>
      )}
    </section>
  );
}

export default function Publicidad() {
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [editando, setEditando] = useState<FilaDeAnuncio | null>(null);
  const [costo, setCosto] = useState('');
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const menu = useMenu();
  // La administración del sistema es a quien el servidor le da «Sistema» (el mismo criterio que la
  // clave de IA): el rol no alcanza, Richard también es OWNER. La API del enlace lo exige igual.
  const esAdministracion =
    user?.role === 'OWNER' && !viendoComo() && Boolean(menu.datos?.rutas.some((r) => r.ruta === '/sistema'));

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['publicidad', periodo],
    queryFn: () => api.reporte(periodo),
  });

  const guardar = useMutation({
    mutationFn: () =>
      api.ponerCosto({ sourceId: editando!.sourceId!, titulo: editando!.titulo, costo: Math.max(0, Math.round(Number(costo) || 0)) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['publicidad'] });
      setEditando(null);
      toast.success('Costo del anuncio guardado');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });

  const descargar = () => {
    if (!data) return;
    const url = URL.createObjectURL(new Blob([api.aCsv(data)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `publicidad-${data.desde.slice(0, 10)}-a-${data.hasta.slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // SUPERFILTRO (Josué, 14-oct): los anuncios por veredicto y si tienen costo, en la URL. El período
  // cambia lo que se mide (otra consulta): se queda arriba, como botones.
  const { filtros, ponerVarios } = useFiltrosEnUrl(
    { buscar: '', veredicto: '', costo: '' },
    { veredicto: Object.keys(VEREDICTO), costo: ['con', 'sin'] },
  );
  const todosLosAnuncios = data?.anuncios ?? [];
  const facetas: Faceta[] = [
    {
      clave: 'veredicto',
      titulo: 'Veredicto',
      todas: 'Todos',
      opciones: conCuentas(Object.entries(VEREDICTO).map(([valor, v]) => ({ valor, etiqueta: v.etiqueta })), todosLosAnuncios, (f, v) => f.veredicto === v),
    },
    {
      clave: 'costo',
      titulo: 'Costo',
      todas: 'Todos',
      opciones: conCuentas(
        [
          { valor: 'con', etiqueta: 'Con costo anotado' },
          { valor: 'sin', etiqueta: 'Sin costo' },
        ],
        todosLosAnuncios,
        (f, v) => (v === 'con') === (f.costo != null),
      ),
    },
  ];
  const qa = filtros.buscar.trim().toLowerCase();
  const anuncios = todosLosAnuncios.filter(
    (f) =>
      (!qa || f.titulo.toLowerCase().includes(qa)) &&
      (!filtros.veredicto || f.veredicto === filtros.veredicto) &&
      (!filtros.costo || (filtros.costo === 'con') === (f.costo != null)),
  );

  const t = data?.total;
  const m = data?.moneda ?? 'US$';

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Publicidad"
        subtitulo="De qué anuncio llega cada chat, qué tan bueno es el cliente que trae y cuál se queda o se va."
        acciones={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border border-surface-border bg-surface p-0.5">
              {PERIODOS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPeriodo(p.id)}
                  aria-pressed={periodo === p.id}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-sm transition-colors',
                    periodo === p.id ? 'bg-brand-600 font-medium text-white' : 'text-slate-600 hover:text-slate-900 dark:text-slate-300',
                  )}
                >
                  {p.etiqueta}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={descargar} disabled={!data}>
              <Download className="mr-1 h-4 w-4" /> Excel
            </Button>
          </div>
        }
        kpis={
          t && (
            <>
              <Kpi titulo="Chats nuevos" valor={numero(t.leads)} pie={`${numero(t.leads - (data?.anuncios.find((a) => !a.sourceId)?.leads ?? 0))} desde anuncios`} />
              <Kpi titulo="Calidad" valor={t.calidad == null ? '—' : `${t.calidad} / 100`} pie={`${numero(t.calientes)} calientes · ${numero(t.frios)} fríos`} />
              <Kpi titulo="Cerraron" valor={numero(t.cerrados)} pie={`cierre ${pct(t.tasaDeCierre)}`} tono="bueno" />
              <Kpi
                titulo="Vendido"
                valor={dinero(t.vendido, m)}
                pie={t.retorno != null ? `retorno ${veces(t.retorno)}` : t.ventas === 0 ? 'sin ventas registradas' : `${numero(t.ventas)} ventas`}
              />
            </>
          )
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} />
        </div>
      ) : isError || !data ? (
        <div className="space-y-3 py-10 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-300">No se pudo cargar el análisis.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          <section className="rounded-lg border border-surface-border bg-surface p-4">
            <h2 className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100">Lo que dicen los números</h2>
            <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-300">
              {data.conclusiones.map((c) => (
                <li key={c} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                  {c}
                </li>
              ))}
            </ul>
          </section>

          {data.anuncios.length > 0 && (
            <Superfiltro
              buscar={filtros.buscar}
              onBuscar={(v) => ponerVarios({ buscar: v })}
              placeholder="Buscar un anuncio…"
              facetas={facetas}
              valores={filtros}
              onCambiar={ponerVarios}
              onLimpiar={() => ponerVarios(sinFiltros(facetas))}
              resultados={textoDeResultados(anuncios.length, data.anuncios.length, ['anuncio', 'anuncios'])}
            />
          )}
          {data.anuncios.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">Sin chats ni anuncios en este período.</p>
          ) : anuncios.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">Ningún anuncio coincide con la búsqueda o los filtros.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-surface-border">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500 dark:bg-slate-900">
                  <tr>
                    <th className="px-3 py-2 font-medium">Anuncio</th>
                    <th className="px-3 py-2 font-medium">Veredicto</th>
                    <th className="px-3 py-2 text-right font-medium">Chats</th>
                    <th className="px-3 py-2 text-right font-medium">Calidad</th>
                    <th className="px-3 py-2 text-right font-medium">Cerraron</th>
                    <th className="px-3 py-2 text-right font-medium">Vendido</th>
                    <th className="px-3 py-2 text-right font-medium">Costo</th>
                    <th className="px-3 py-2 text-right font-medium">Por chat</th>
                    <th className="px-3 py-2 text-right font-medium">Retorno</th>
                  </tr>
                </thead>
                <tbody>
                  {anuncios.map((f) => (
                    <tr key={f.sourceId ?? 'directo'} className="border-t border-surface-border align-top dark:border-slate-800">
                      <td className="max-w-[240px] px-3 py-2">
                        <div className="flex items-center gap-1 font-medium text-slate-800 dark:text-slate-100">
                          <span className="truncate" title={f.titulo}>
                            {f.titulo}
                          </span>
                          {f.url && (
                            <a href={f.url} target="_blank" rel="noreferrer" className="shrink-0 text-slate-400 hover:text-brand-600" aria-label="Ver el anuncio">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {f.sourceId ? (
                          <span className={cn('inline-block rounded-full px-2 py-0.5 text-xs font-medium', VEREDICTO[f.veredicto].clase)}>
                            {VEREDICTO[f.veredicto].etiqueta}
                          </span>
                        ) : null}
                        <p className="mt-1 max-w-[260px] text-xs text-slate-500">{f.motivo}</p>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{numero(f.leads)}</td>
                      <td className="px-3 py-2 text-right">
                        <Calidad f={f} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-green-700 dark:text-green-300">
                        {numero(f.cerrados)} <span className="text-xs text-slate-400">({pct(f.tasaDeCierre)})</span>
                      </td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">{dinero(f.vendido, m)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {!f.sourceId ? (
                          '—'
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditando(f);
                              setCosto(f.costo == null ? '' : String(f.costo));
                            }}
                            className="inline-flex items-center gap-1 text-brand-700 underline-offset-2 hover:underline dark:text-brand-300"
                          >
                            {f.costo == null ? 'Anotar' : dinero(f.costo, m)}
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{dinero(f.costoPorLead, m)}</td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums',
                          f.retorno != null && (f.retorno >= 1 ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-400'),
                        )}
                      >
                        {veces(f.retorno)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-slate-400">
            El anuncio es el primero por el que escribió cada cliente (Meta lo manda en el primer mensaje). No cuentan los
            contactos marcados «Es personal» ni los que llegaron en tu Excel. La calidad suma los cerrados y calientes y medio
            punto por cada tibio, sobre 100; «largo plazo» cuenta como frío. «Cerraron» es quien quedó «Cerrado» (por su
            temperatura o en Clientes). Las ventas no se registran en este sistema: «Vendido» queda en 0 y no hay retorno, así
            que el veredicto compara el cierre de cada anuncio con el promedio. El costo anotado es todo lo invertido en el
            anuncio: con costo, «Por chat» y el veredicto se miden con todo lo que el anuncio trajo desde que corre.
          </p>
        </>
      )}

      {esAdministracion && <EnlaceSilvaService />}

      <Modal
        open={!!editando}
        onClose={() => setEditando(null)}
        title="Cuánto costó este anuncio"
        description={editando?.titulo}
        footer={
          <>
            <Button variant="outline" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button onClick={() => guardar.mutate()} loading={guardar.isPending}>
              Guardar
            </Button>
          </>
        }
      >
        <label className="block space-y-1">
          <span className="text-xs text-slate-500">Lo invertido en este anuncio, en {m}</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={costo}
            onChange={(e) => setCosto(e.target.value)}
            className="w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
      </Modal>
    </div>
  );
}
